/**
 * ADP payroll adapter (`payroll.adp`) — Time Evaluation Phase 4b.
 *
 * ── Be honest about what this integration IS ──────────────────────────────────
 * ADP Workforce Now has **no general-purpose public paydata WRITE API** on most
 * commercial contracts. Paydata reaches WFN either as a FILE IMPORT that a
 * payroll administrator uploads, or through ADP's own Data Connector /
 * Marketplace program (a per-client commercial arrangement with its own
 * certification). There is no ADP credential anywhere in this system.
 *
 * So a downloadable, correctly-shaped CSV **is** the correct v1, and this
 * adapter deliberately makes NO network call. It does not stub one either: a
 * fake `fetch` to a plausible ADP URL would make the Integrations status page
 * claim a connection that does not exist, and the first person to trust it would
 * be a payroll admin on a Thursday.
 *
 * `healthCheck()` is therefore a CONFIGURATION probe, not a liveness probe: does
 * the crosswalk have any ADP rows, and is there a usable mapping profile. Those
 * are the two things that actually decide whether a file can be produced.
 *
 * ── What it does ──────────────────────────────────────────────────────────────
 *   pullWorkers()          reads `external_id_map` (source_system='adp') via
 *                          `services/adpWorkerCrosswalkService`
 *   buildPaydataFile()     the real leg — `EvaluatedTimeRecord[]` -> CSV
 *   pushPayrollBatch()     the `PayrollAdapter` contract method. Accepts the
 *                          vendor-neutral `PayrollBatch` and produces the file.
 *                          LOSSY (see `payrollBatchLinesToRecords`); prefer
 *                          `buildPaydataFile` whenever the evaluated records
 *                          are in hand.
 *
 * Registration happens in `integrations/registerAdapters.ts`, which must be
 * imported once at app bootstrap — see the warning in that file.
 */
import {
  registerAdapter,
  type AdapterHealth,
  type PayrollAdapter,
  type PayrollBatch,
  type PayrollWorker,
  type PostResult,
} from '../adapterContracts'
import { WFN_PAYDATA_PRESET } from '@/modules/timesheet/lib/adpPaydataPreset'
import {
  validateAdpProfile,
  type AdpMappingProfile,
} from '@/modules/timesheet/lib/adpMappingProfile'
import {
  listAdpWorkerCrosswalk,
  type AdpWorkerCrosswalkEntry,
} from '@/modules/timesheet/services/adpWorkerCrosswalkService'
import type { PayrollPeriod } from '@/modules/timesheet/payroll/buildPayrollBatch'
import { payrollBatchLinesToRecords } from '@/modules/timesheet/payroll/evaluatedToPayrollBatch'
import {
  buildAdpPaydataFile,
  type AdpPaydataFile,
} from '@/modules/timesheet/payroll/serializeAdpPaydata'
import type { EvaluatedTimeRecord } from '@/modules/timesheet/types/timeEvaluation.types'

/** The catalog key. Must match `INTEGRATIONS_CATALOG`, or registration is ignored. */
export const ADP_PAYROLL_ADAPTER_KEY = 'payroll.adp'

export interface AdpPayrollAdapterConfig {
  /**
   * The client's mapping profile. Defaults to `WFN_PAYDATA_PRESET`, which has an
   * EMPTY `companyCodeByTenant` on purpose — so the default adapter reports
   * unhealthy until a real Co Code is configured, rather than inventing one and
   * routing a payroll into the wrong ADP company.
   */
  profile?: AdpMappingProfile
  /** Async profile source (Phase 5's `payroll_export_profiles` reader). Wins over `profile`. */
  loadProfile?: () => Promise<AdpMappingProfile | null>
  /** Crosswalk reader. Injectable so this is unit-testable without Supabase. */
  listCrosswalk?: () => Promise<AdpWorkerCrosswalkEntry[]>
  /**
   * Called with the produced file. The adapter itself never touches the DOM —
   * Phase 5 passes a `downloadFile` closure here.
   */
  onFile?: (file: AdpPaydataFile) => void | Promise<void>
}

/** The contract method plus the premium-aware leg the contract cannot express. */
export interface AdpPayrollAdapter extends PayrollAdapter {
  /** The real export: evaluated records straight to the ADP grid. Nothing is lost. */
  buildPaydataFile(args: {
    records: readonly EvaluatedTimeRecord[]
    period: PayrollPeriod
    tenant: string | null
    workers?: Record<string, PayrollWorker | undefined>
    profile?: AdpMappingProfile
    sequence?: number | string | null
  }): Promise<AdpPaydataFile>
  /** The profile this adapter would serialize with right now. */
  resolveProfile(): Promise<AdpMappingProfile>
}

function crosswalkToWorkers(rows: readonly AdpWorkerCrosswalkEntry[]): PayrollWorker[] {
  return rows.map((r) => ({
    externalId: r.employeeId,
    // The crosswalk carries no name — `external_id_map` is an id table. The
    // caller joins `users` when it needs a display name; an invented one here
    // would show up in the exceptions report as fact.
    name: '',
    payrollId: r.fileNumber,
    payrollTenant: r.coCode,
  }))
}

export function createAdpPayrollAdapter(cfg: AdpPayrollAdapterConfig = {}): AdpPayrollAdapter {
  const listCrosswalk = cfg.listCrosswalk ?? listAdpWorkerCrosswalk

  async function resolveProfile(): Promise<AdpMappingProfile> {
    if (cfg.loadProfile) {
      const loaded = await cfg.loadProfile()
      if (loaded) return loaded
    }
    return cfg.profile ?? WFN_PAYDATA_PRESET
  }

  return {
    key: ADP_PAYROLL_ADAPTER_KEY,
    domain: 'payroll',
    vendor: 'ADP',

    /**
     * CONFIGURATION probe, not a network probe (see the file header). Reports
     * unhealthy when there are no ADP crosswalk rows (every worker would block
     * and the file would be empty) or when the profile has validation problems
     * — most commonly the missing Co Code.
     */
    async healthCheck(): Promise<AdapterHealth> {
      try {
        const [rows, profile] = await Promise.all([listCrosswalk(), resolveProfile()])
        const problems = validateAdpProfile(profile)
        const bits = [
          `${rows.length} ADP crosswalk row(s)`,
          `profile "${profile.name}"`,
          problems.length === 0 ? 'profile clean' : `${problems.length} profile problem(s)`,
        ]
        const detail =
          `${bits.join(', ')}. File import only — ADP WFN has no general paydata write API, ` +
          `so no endpoint is contacted.` +
          (problems.length > 0 ? ` First: ${problems[0]}` : '') +
          (rows.length === 0
            ? ' Import the ADP File # / Co Code crosswalk before running an export.'
            : '')
        return { ok: rows.length > 0 && problems.length === 0, detail }
      } catch (err) {
        return {
          ok: false,
          detail: `ADP crosswalk/profile read failed: ${err instanceof Error ? err.message : String(err)}`,
        }
      }
    },

    async pullWorkers(): Promise<PayrollWorker[]> {
      return crosswalkToWorkers(await listCrosswalk())
    },

    async resolveProfile() {
      return resolveProfile()
    },

    async buildPaydataFile(args): Promise<AdpPaydataFile> {
      const profile = args.profile ?? (await resolveProfile())
      let workers = args.workers
      if (!workers) {
        workers = {}
        for (const w of crosswalkToWorkers(await listCrosswalk())) workers[w.externalId] = w
      }
      const file = buildAdpPaydataFile({
        records: args.records,
        profile,
        period: args.period,
        tenant: args.tenant,
        workers,
        sequence: args.sequence ?? null,
      })
      await cfg.onFile?.(file)
      return file
    },

    /**
     * The `PayrollAdapter` contract leg. NO HTTP — it produces the file and
     * reports what went into it. `posted` counts DATA rows (the header row is
     * not a record); `skipped` counts blocked workers; `errors` carries their
     * one-line reasons so a caller that only has a `PostResult` still learns why.
     */
    async pushPayrollBatch(batch: PayrollBatch): Promise<PostResult> {
      const profile = await resolveProfile()
      const period: PayrollPeriod = batch.period
      const workers: Record<string, PayrollWorker> = {}
      for (const line of batch.lines) {
        workers[line.workerExternalId] = {
          externalId: line.workerExternalId,
          name: '',
          payrollId: line.payrollId,
          sapEmployeeNo: line.sapEmployeeNo ?? null,
          payrollTenant: line.payrollTenant ?? null,
        }
      }
      // The batch may hold several tenants; a File # is only unique within a Co
      // Code, so serialize the tenant this batch actually carries. A caller with
      // more than one tenant must partition first
      // (`partitionPayrollBatchByTenant`) and call once per partition.
      const tenants = [...new Set(batch.lines.map((l) => l.payrollTenant ?? null))]
      const tenant = tenants.length === 1 ? tenants[0]! : null
      const file = buildAdpPaydataFile({
        records: payrollBatchLinesToRecords(batch.lines, period),
        profile,
        period,
        tenant,
        workers,
      })
      await cfg.onFile?.(file)
      const dataRows = file.rows.length - (profile.includeHeaderRow ? 1 : 0)
      const errors = file.blockers.map((b) => `${b.workerName} (${b.employeeId}): ${b.reason} — ${b.detail}`)
      if (tenants.length > 1) {
        errors.unshift(
          `Batch spans ${tenants.length} payroll tenants; partition with ` +
            `partitionPayrollBatchByTenant and push one file per tenant.`,
        )
      }
      return { posted: Math.max(0, dataRows), skipped: file.blockers.length, errors }
    },
  }
}

/**
 * Self-registration on import. `registerAdapters.ts` imports this module, which
 * is what actually populates the registry — see the warning there.
 */
registerAdapter(ADP_PAYROLL_ADAPTER_KEY, () => createAdpPayrollAdapter())
